import React, { useEffect, useRef, useState } from 'react';
import { 
  FaShieldAlt, FaLock, FaDatabase, FaCookie, FaUserSecret,
  FaEnvelope, FaPhone, FaMapMarkerAlt, FaArrowRight,
  FaCheckCircle, FaGlobe, FaEye, FaUserCheck, FaRegClock,
  FaMobileAlt, FaDesktop, FaServer
} from 'react-icons/fa';
import { MdVerified, MdPrivacyTip, MdSecurity } from 'react-icons/md';

const PrivacyPolicy = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  const [lastUpdated] = useState("15 April 2024");

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const sections = [
    {
      title: "Information We Collect",
      icon: <FaDatabase className="text-blue-500" />,
      content: [
        "Personal Information: Name, email address, phone number, date of birth, address, and academic records",
        "Academic Information: Educational qualifications, marksheets, course preferences, admission details",
        "Technical Information: IP address, browser type, device information, and cookies",
        "Usage Data: Pages visited, time spent, and interaction with our website"
      ]
    },
    {
      title: "How We Use Your Information",
      icon: <FaEye className="text-blue-500" />,
      content: [
        "Process admission applications and registrations",
        "Communicate important academic updates and announcements",
        "Improve our educational services and facilities",
        "Maintain academic records and results",
        "Send notifications about exams, events, and holidays",
        "Respond to your inquiries and provide customer support",
        "Comply with legal obligations"
      ]
    },
    {
      title: "Cookies & Tracking Technologies",
      icon: <FaCookie className="text-blue-500" />,
      content: [
        "Our website uses cookies to enhance user experience and analyze website traffic",
        "We use session cookies to maintain your login session",
        "Google Analytics helps us understand how visitors interact with our site",
        "You can disable cookies through your browser settings",
        "Disabling cookies may affect certain features of our website"
      ]
    },
    {
      title: "Data Security",
      icon: <MdSecurity className="text-blue-500" />,
      content: [
        "We implement industry-standard security measures to protect your information",
        "Data is stored on secure servers with encryption",
        "Access is restricted to authorized personnel only",
        "Regular security audits and updates are performed",
        "We use SSL/TLS encryption for data transmission"
      ]
    },
    {
      title: "Third-Party Disclosure",
      icon: <FaGlobe className="text-blue-500" />,
      content: [
        "We do not sell, trade, or transfer your personal information to third parties",
        "Trusted partners who assist in website operation agree to keep information confidential",
        "We may share information when required by law or to protect our rights",
        "Service providers may have access to data for specific tasks"
      ]
    },
    {
      title: "Data Retention",
      icon: <FaRegClock className="text-blue-500" />,
      content: [
        "We retain your information as long as necessary for the purposes outlined",
        "Academic records are kept as per university guidelines",
        "You may request deletion of your personal information",
        "Some data may be retained for legal or legitimate business purposes"
      ]
    },
    {
      title: "Your Rights",
      icon: <FaUserCheck className="text-blue-500" />,
      content: [
        "Right to access your personal information",
        "Right to correct inaccurate or incomplete information",
        "Right to request deletion of your information",
        "Right to object to processing of your information",
        "Right to withdraw consent at any time",
        "Right to data portability"
      ]
    },
    {
      title: "Children's Privacy",
      icon: <FaUserSecret className="text-blue-500" />,
      content: [
        "Our website is not intended for children under 13 years of age",
        "We do not knowingly collect personal information from children under 13",
        "If you believe we have collected information from a child under 13, please contact us",
        "We will promptly delete any such information"
      ]
    }
  ];

  return (
    <section ref={sectionRef} className="w-full bg-gradient-to-br from-blue-50 via-white to-blue-100 py-16 min-h-screen">
      <div className="w-full px-6 lg:px-12">
        <div className="max-w-5xl mx-auto">
          
          {/* Decorative Top */}
          <div className="flex justify-center gap-3 mb-6">
            <div className="w-12 h-1 bg-blue-500 rounded-full"></div>
            <div className="w-8 h-1 bg-blue-600 rounded-full"></div>
            <div className="w-6 h-1 bg-blue-700 rounded-full"></div>
          </div>

          {/* Breadcrumb */}
          <div className={`flex items-center justify-center gap-2 text-xs mb-6 transition-all duration-500 ${
            isVisible ? 'translate-y-0 opacity-100' : '-translate-y-10 opacity-0'
          }`}>
            <span className="text-gray-500">Home</span>
            <span className="text-gray-400">/</span>
            <span className="text-blue-600 font-semibold">Privacy Policy</span>
          </div>

          {/* Heading */}
          <div className={`text-center mb-10 transition-all duration-700 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}>
            <div className="flex justify-center mb-3">
              <div className="bg-gradient-to-r from-blue-600 to-blue-800 p-3 rounded-2xl shadow-lg">
                <MdPrivacyTip className="text-white text-3xl" />
              </div>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-3">
              <span className="text-blue-800">Privacy Policy</span>
            </h2>
            <div className="w-24 h-1 bg-blue-500 rounded-full mx-auto"></div>
            <p className="text-gray-600 text-sm mt-4">
              Last Updated: {lastUpdated}
            </p>
          </div>

          {/* Introduction Card */}
          <div className={`bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 mb-8 transition-all duration-500 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}>
            <div className="flex items-start gap-3">
              <FaShieldAlt className="text-blue-600 text-2xl mt-1" />
              <div>
                <h3 className="text-lg font-bold text-gray-800 mb-2">Introduction</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  At <span className="font-semibold text-blue-600">BNS Mahila Maha Vidyalaya</span>, we are committed to protecting your privacy. 
                  This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit 
                  our website or use our services. Please read this privacy policy carefully. If you do not agree with the 
                  terms of this privacy policy, please do not access the site.
                </p>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className={`space-y-5 transition-all duration-500 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}>
            {sections.map((section, idx) => (
              <div key={idx} className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-all">
                <div className="border-l-4 border-blue-500 p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                      {section.icon}
                    </div>
                    <h3 className="text-lg font-bold text-gray-800">{section.title}</h3>
                  </div>
                  <ul className="space-y-2 pl-11">
                    {section.content.map((item, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <FaCheckCircle className="text-green-500 text-xs mt-0.5 shrink-0" />
                        <span className="text-gray-600 text-sm">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>

          {/* Information Collection Methods */}
          <div className={`mt-8 bg-white rounded-xl shadow-md overflow-hidden transition-all duration-500 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}>
            <div className="border-l-4 border-blue-500 p-5">
              <h3 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
                <FaServer className="text-blue-500" />
                Information Collection Methods
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <FaDesktop className="text-blue-500" />
                    <span className="font-semibold text-gray-700 text-sm">Direct Collection</span>
                  </div>
                  <p className="text-gray-500 text-xs">Information you provide directly through forms, registrations, and communications</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <FaMobileAlt className="text-blue-500" />
                    <span className="font-semibold text-gray-700 text-sm">Automatic Collection</span>
                  </div>
                  <p className="text-gray-500 text-xs">Information automatically collected through cookies and analytics tools</p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Section */}
          <div className={`mt-8 bg-gradient-to-r from-blue-600 to-blue-800 rounded-2xl p-6 text-white transition-all duration-500 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}>
            <div className="text-center">
              <h3 className="text-xl font-bold mb-3">Have Questions About Privacy?</h3>
              <p className="text-blue-100 text-sm mb-4">Contact our Data Protection Officer for privacy-related concerns</p>
              <div className="flex flex-wrap justify-center gap-4">
                <div className="flex items-center gap-2">
                  <FaPhone className="text-yellow-400" />
                  <span className="text-sm">+91 7080 100 333</span>
                </div>
                <div className="flex items-center gap-2">
                  <FaEnvelope className="text-yellow-400" />
                  <span className="text-sm">privacy@bnsmahila.edu</span>
                </div>
                <div className="flex items-center gap-2">
                  <FaMapMarkerAlt className="text-yellow-400" />
                  <span className="text-sm">Akhari Bai-Pass, Varanasi, UP 221011</span>
                </div>
              </div>
            </div>
          </div>

          {/* Policy Updates Note */}
          <div className={`mt-6 bg-yellow-50 rounded-lg p-4 text-center transition-all duration-500 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}>
            <p className="text-yellow-700 text-xs">
              <span className="font-semibold">Note:</span> We may update this Privacy Policy from time to time. 
              Please review this page periodically for any changes. Your continued use of our website constitutes acceptance of the updated policy.
            </p>
          </div>

          {/* Bottom Decorative */}
          <div className="mt-10 flex justify-center gap-2">
            <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"></div>
            <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
            <div className="w-2 h-2 bg-blue-700 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
        .animate-bounce {
          animation: bounce 1s ease-in-out infinite;
        }
      `}</style>
    </section>
  );
};

export default PrivacyPolicy;